import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {SemVer} from 'semver';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {Audience, BENCHLING_APPS_WEB_URL, DEFAULT_APP_VERSION} from 'benchling-apps/BenchlingAppDAO';
import {openBenchlingAppDeveloperPage} from 'benchling-apps/lib';
import BenchlingConfig from 'BenchlingConfig';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';

import * as AppDefinitionActions from '../../AppDefinitionActions';
import * as BenchlingAppActions from '../../BenchlingAppActions';
import {AppDefinitionVersion} from '../BenchlingAppsPage';
import {DEFAULT_AUDIENCE} from './AudienceRadioButtons';
import {biggestSoFar, incrementPatchVersion} from './BenchlingAppVersionInput';
import CreateAppChoiceButtons from './CreateAppChoiceButtons';
import CreateAppForm from './CreateAppForm';
import {AppOrVersion, ButtonType} from './CreateAppTypes';

interface CreateAppModalProps {
  appId?: string;
  currentVersions?: Array<AppDefinitionVersion>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export enum ModalType {
  APP_FROM_SCRATCH,
  APP_FROM_MANIFEST,
  VERSION_FROM_DRAFT,
  VERSION_FROM_MANIFEST,
}

export default function CreateAppModal(props: CreateAppModalProps): JSX.Element {
  const currentVersions = (props.currentVersions ?? []).map((v) => new SemVer(v.versionId));
  const biggestSemanticVersion = biggestSoFar(currentVersions);
  const defaultSemanticVersion = incrementPatchVersion(
    biggestSemanticVersion == null ? DEFAULT_APP_VERSION : biggestSemanticVersion.format()
  );
  const biggestVersion = (props.currentVersions ?? []).find(
    (v) => v.versionId === (biggestSemanticVersion?.format() ?? DEFAULT_APP_VERSION)
  );
  const appOrVersion = props.appId === undefined ? AppOrVersion.APP : AppOrVersion.VERSION;
  const isApp = appOrVersion === AppOrVersion.APP;
  const appOrDraftString = isApp ? 'app' : 'draft';

  const defaultType =
    appOrVersion === AppOrVersion.APP ? ModalType.APP_FROM_SCRATCH : ModalType.VERSION_FROM_DRAFT;

  const [modalType, setModalType] = useState<ModalType>(defaultType);
  const [isFormValid, setFormIsValid] = useState<boolean>(!isApp);
  const [name, setName] = useState<string | null>(biggestVersion?.name ?? null);
  const [description, setDescription] = useState<string | null>(biggestVersion?.description ?? null);
  const [version, setVersion] = useState<string>(defaultSemanticVersion);
  const [audience, setAudience] = useState<Audience>(DEFAULT_AUDIENCE);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const fromManifest =
    modalType === ModalType.APP_FROM_MANIFEST || modalType === ModalType.VERSION_FROM_MANIFEST;

  function onChangeModalType(clickedButton: ButtonType): void {
    setModalType(modalTypeForButton(clickedButton));
  }

  function onChangeFile(file: File | null, newVersion: string | null): void {
    setUploadedFile(file);
    setVersion(newVersion ?? version);
  }

  function modalTypeForButton(clickedButton: ButtonType): ModalType {
    const isManifest = clickedButton === ButtonType.FROM_MANIFEST;
    switch (appOrVersion) {
      case AppOrVersion.APP: {
        return isManifest ? ModalType.APP_FROM_MANIFEST : ModalType.APP_FROM_SCRATCH;
      }
      case AppOrVersion.VERSION: {
        return isManifest ? ModalType.VERSION_FROM_MANIFEST : ModalType.VERSION_FROM_DRAFT;
      }
      default:
        return appOrVersion satisfies never;
    }
  }

  async function handleSubmit(): Promise<void> {
    if (!isFormValid) {
      return;
    }
    if (fromManifest) {
      await handleSubmitFromManifest();
    } else {
      await handleSubmitFromScratch();
    }
  }

  async function handleSubmitFromScratch(): Promise<void> {
    if (isSubmitting) {
      return;
    }
    let taskId: string | null = null;
    try {
      taskId = TrayTaskActions.createTrayTask({
        heading: isApp ? 'Creating app' : `Creating version ${name} ${version}`,
      });
      const successMessage = isApp ? `Created app ${name}` : `Created version ${name} ${version}`;

      setIsSubmitting(true);

      const data = {
        // name should never be null if isFormValid is true
        name: name ?? '<no name>',
        description: description ?? '',
      };
      let appId: string;
      if (isApp) {
        if (BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION) {
          const appDef = await AppDefinitionActions.createAppDefinition({...data, audience});
          appId = appDef.id;
        } else {
          const app = await BenchlingAppActions.deprecatedCreate({...data, audience});
          appId = app.id;
        }
      } else if (props.appId === undefined) {
        // this should never happen - an appId is a required property when props.appOrVersion == VERSION
        throw new Error('Cannot create version with undefined appId.');
      } else if (BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION) {
        const appDef = await AppDefinitionActions.updateAppDefinitionVersion(props.appId, version, {
          ...data,
          versionToInheritFrom: biggestSemanticVersion?.format(),
        });
        appId = appDef.id;
      } else {
        const app = await BenchlingAppActions.deprecatedUpdateDefinitionVersion(props.appId, version, data);
        appId = app.id;
      }
      notifications.success(successMessage);
      openBenchlingAppDeveloperPage(appId);
    } finally {
      if (taskId) {
        TrayTaskActions.removeTrayTask(taskId);
      }
      setIsSubmitting(false);
      props.onRequestSubmit();
    }
  }

  async function handleSubmitFromManifest(): Promise<void> {
    if (uploadedFile == null || isSubmitting) {
      return;
    }
    const taskId = TrayTaskActions.createTrayTask({
      heading: isApp ? 'Creating app' : `Uploading app manifest for version ${version}`,
    });

    const successMessage = isApp
      ? `Successfully created app from manifest`
      : `Successfully created version ${version} from manifest`;

    try {
      setIsSubmitting(true);
      const importUrl = await ImportUtil.upload(uploadedFile);
      if (importUrl) {
        const data = {location: importUrl};
        let appId: string;
        if (isApp) {
          if (BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION) {
            const appDef = await AppDefinitionActions.createAppDefinitionFromManifest({...data, audience});
            appId = appDef.id;
          } else {
            const app = await BenchlingAppActions.deprecatedCreateAppFromManifest({...data, audience});
            appId = app.id;
          }
        } else if (props.appId === undefined) {
          // this should never happen - an appId is a required property when props.appOrVersion == VERSION
          throw new Error('Cannot create version from manifest with undefined appId.');
        } else if (BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION) {
          const appDef = await AppDefinitionActions.updateAppDefinitionVersionFromManifest(
            props.appId,
            version,
            data
          );
          appId = appDef.id;
        } else {
          const app = await BenchlingAppActions.deprecatedUpdateDefinitionVersionFromManifest(
            props.appId,
            version,
            data
          );
          appId = app.id;
        }
        TrayTaskActions.removeTrayTask(taskId);
        notifications.success(successMessage);
        const benchlingAppPageURL = `${BENCHLING_APPS_WEB_URL}/${appId}`;
        navigateToURL(benchlingAppPageURL);
      }
    } catch (err) {
      if (net.isServerRequestError(err)) {
        TrayTaskActions.updateTrayTask(taskId, {
          progress: TrayTaskConstants.FAILURE_PROGRESS,
          error:
            notifications.getFriendlyError(err) ??
            'Something went wrong uploading the manifest. Please try again',
        });
      } else {
        throw err;
      }
    } finally {
      setIsSubmitting(false);
      props.onRequestSubmit();
    }
  }

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      bodyPadded={false}
      large={true}
      title={`Create a new ${appOrDraftString}`}
      buttons={[
        {
          text: 'Create',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: !isFormValid,
          dataTestAttrs: {component: 'CreateAppModal', element: 'createButton'},
        },
      ]}
      dataTestAttrs={{component: 'CreateAppModal', element: 'root'}}
      analyticsModule={ANALYTICS_MODULE.BENCHLING_APPS}
      analyticsModalType="create_app"
      analyticsMetadata={{isApp}}
    >
      <Row className={css(styles.formContainer)}>
        <CreateAppChoiceButtons appOrVersion={appOrVersion} onChangeType={onChangeModalType} />
        <CreateAppForm
          modalType={modalType}
          name={name || ''}
          description={description || ''}
          version={version}
          currentVersions={props.currentVersions?.map((v) => v.versionId) ?? []}
          audience={audience}
          uploadedFile={uploadedFile}
          onChangeFile={onChangeFile}
          onChangeAudience={setAudience}
          onChangeIsValid={setFormIsValid}
          onChangeVersion={setVersion}
          onChangeName={setName}
          onChangeDescription={setDescription}
        />
      </Row>
    </Modal>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    paddingLeft: 16,
    paddingRight: 16,
    marginBottom: 0,
  },
});
